import { computed, ref, type ComputedRef } from 'vue'
import type {
  ItemRow, OrgDocumentsPayload, SheetGroupSummary, ConversationGroupSummary,
} from '~~/shared/items/itemRow'
import { mapWith, pruneIdLists, pruneIds, type EntityCache } from '~/stores/entityCache'
import { bucketOrgDocuments } from '~/composables/documents/bucketOrgDocuments'
import { PAGE_SIZE, type ApiFetch, type ListingParams, type LoadState } from '~/composables/documents/browser'

export interface OrgGroupsDeps {
  apiFetch: ApiFetch
  cache: EntityCache<ItemRow>
  /** The active sort and type/state lens, in their wire form. */
  listingParams: ComputedRef<ListingParams>
  /** Register pending rows with the indexing store's poller. */
  trackPending: (rows: ItemRow[]) => void
}

/**
 * The org-wide document set the grouped views draw from, and which of those are linked to no case.
 *
 * The store deliberately does NOT hold groups any more (MAR-125). A group is a case or a client
 * plus its membership, and both of those belong to their own stores — holding a second, server-built
 * copy here is exactly why a newly-created case did not appear in the sidebar. Grouping is derived
 * in `useDocumentGroups()`; this holds the buckets it reads.
 */
export function useOrgGroups({ apiFetch, cache, listingParams, trackPending }: OrgGroupsDeps) {
  const groupedIds = ref<string[]>([])
  /**
   * Where each org-listed document sits in THE SERVER'S ORDER (AlexAmin/marcus#169).
   *
   * `groupedIds` arrives from `listOrgDocuments` already ordered by the active sort — the same
   * ordering its per-group `row_number()` windows used to choose which rows the one-page cap keeps,
   * so the rows a band can show and the order it shows them in are one decision, made in SQL.
   *
   * This index is how that survives the trip through `useDocumentGroups`, which resolves a band's
   * rows by looking ids up in the cache: without it a band's order is whatever order its id list
   * happens to have (link time, for a case), and the client would silently re-impose it over the
   * server's. An id the org listing did not return — a document reached only through the flat folder
   * listing, or one just created locally — has no rank and sorts last, which is the honest place for
   * a row the ordering never saw.
   */
  const groupedRank = computed(() => {
    const rank = new Map<string, number>()
    groupedIds.value.forEach((id, i) => rank.set(id, i))
    return rank
  })
  const ungroupedIds = ref<string[]>([])
  /** True size of the "no case" bucket — `ungroupedIds` is capped at one page (MAR-93). */
  const ungroupedTotal = ref(0)
  /**
   * Documents linked to a chat thread — the "From your conversations" group. Capped at one page,
   * hence its own server-shipped total.
   *
   * "Linked", not "linked and nothing else" (AlexAmin/marcus#150). This was `conversationOnlyIds`
   * and excluded anything that also had a case or a client, which is the normal state under #83 —
   * so the group was permanently empty. A thread is a home; it just is not an exclusive one.
   */
  const conversationLinkedIds = ref<string[]>([])
  const conversationTotal = ref(0)
  /**
   * Sheets, one group each (AlexAmin/marcus#150) — `sheetDocumentIds` maps a sheet to the documents
   * of it we have loaded, `sheetGroupsMeta` carries each sheet's name and its UNCAPPED size. Per
   * sheet rather than one "from your sheets" pile because a sheet is a named, durable, user-made
   * container — exactly what a case or a client is, and they each get their own group.
   */
  const sheetDocumentIds = ref<Map<string, string[]>>(new Map())
  const sheetGroupsMeta = ref<SheetGroupSummary[]>([])
  /** Documents linked to ANY sheet, counted once each — the finder's one "de tus hojas" signpost. */
  const sheetLinkedIds = ref<string[]>([])
  const sheetTotal = ref(0)
  /**
   * One group per chat thread (AlexAmin/marcus#150 round 2) — the same shape as the sheet pair
   * above, and for the same reason: a thread is selectable as its own group mode, not only visible
   * through the single aggregate bucket `conversationLinkedIds` still feeds.
   */
  const conversationDocumentIds = ref<Map<string, string[]>>(new Map())
  const conversationGroupsMeta = ref<ConversationGroupSummary[]>([])
  /**
   * Documents attached to NOTHING — no case, no client, no conversation, no sheet. Project mode's
   * virtual "Sin cliente" group (`useDocumentGroups`, MAR-1006) is built from exactly these. They are
   * also in `ungroupedIds`, because they have no case either — the two lists answer different
   * questions and case mode asks the other one.
   */
  const unattachedIds = ref<string[]>([])
  const unattachedTotal = ref(0)
  /**
   * Client id → the documents linked DIRECTLY to it (`client_documents`), and how many there really
   * are. Documents reached through one of the client's CASES are NOT in here — that membership lives
   * on the case rows, and `useDocumentGroups` unions the two.
   *
   * The ids are capped at one page per client; `clientDirectTotals` is not, which is what keeps the
   * "+N more" honest for a client holding hundreds of documents.
   */
  const clientDirectIds = ref<Map<string, string[]>>(new Map())
  const clientDirectTotals = ref<Map<string, number>>(new Map())
  /**
   * Per case id: how many documents the case holds UNDER THE ACTIVE FILTER (AlexAmin/marcus#169).
   *
   * `Case.documentIds.length` is the other answer to the same question and stays the fallback for a
   * case the server has not named — but `listCases`, where it comes from, knows nothing about the
   * finder's type/state lens, so under a filter it counts a population the band is not rendering and
   * "+N más" promises documents the filter excluded.
   */
  const caseFilteredTotals = ref<Map<string, number>>(new Map())

  const groupsLoading = ref(false)
  /** Whether the grouped seed ANSWERED (AlexAmin/marcus#469) — see `LoadState`. */
  const groupsState = ref<LoadState>('idle')
  const groupsFailed = computed(() => groupsState.value === 'failed')

  /** Every document a grouped view can show, resolved. Bucketing is `useDocumentGroups()`'s job. */
  const groupedDocuments = computed<ItemRow[]>(() => cache.resolve(groupedIds.value))
  /** Documents linked to no case — case mode's trailing bucket. NOT "attached to nothing": since #83
   *  a document can be filed under a client without being in any case, and it belongs here. */
  const ungroupedDocuments = computed<ItemRow[]>(() => cache.resolve(ungroupedIds.value))

  /**
   * A document gained a home of some kind, so it is no longer attached to nothing.
   *
   * The reverse — a document losing its LAST link — is not knowable from any one unlink site
   * (whether it is attached elsewhere is exactly what the caller does not hold), so `fetchGroups`
   * reconciles that direction, the same contract `unlinkFromClient` already keeps. The one exception
   * is a case delete, whose response names the documents it left with no home (`markUnattached`).
   */
  function dropUnattached(id: string) {
    if (!unattachedIds.value.includes(id)) return
    unattachedIds.value = unattachedIds.value.filter(x => x !== id)
    unattachedTotal.value = Math.max(0, unattachedTotal.value - 1)
  }

  /**
   * The server says this document is now attached to NOTHING — a case delete left it with no home
   * (MAR-1006, `CaseDeleteResult.unattachedItemIds`). It joins the "Sin cliente" group on the same
   * frame. Idempotent; a document this cache has never seen is left for the next `fetchGroups`.
   */
  function markUnattached(id: string) {
    if (unattachedIds.value.includes(id) || !cache.has(id)) return
    unattachedIds.value = [id, ...unattachedIds.value]
    unattachedTotal.value = unattachedTotal.value + 1
  }

  /** A document is now linked to at least one case — it is out of the "no case" bucket, and out of
   *  the attached-to-nothing set with it: a case is one of the four homes. */
  function unmarkUngrouped(id: string) {
    dropUnattached(id)
    if (!ungroupedIds.value.includes(id)) return
    ungroupedIds.value = ungroupedIds.value.filter(x => x !== id)
  }

  /** A document lost its last case link — back to the "no case" bucket, whatever else it is
   *  attached to. Project mode groups by client and has no trailing bucket to update. */
  function markUngrouped(id: string) {
    if (ungroupedIds.value.includes(id) || !cache.has(id)) return
    ungroupedIds.value = [id, ...ungroupedIds.value]
  }

  /**
   * Mirror a DIRECT client link locally, so the browser's client group re-derives on the same frame
   * rather than waiting for a refetch — the same contract `casesStore.addCaseDocument` honours for
   * case links. Idempotent, like the endpoint it mirrors.
   */
  function linkToClient(clientId: string, itemId: string) {
    const held = clientDirectIds.value.get(clientId) ?? []
    if (!held.includes(itemId)) {
      clientDirectIds.value = mapWith(clientDirectIds.value, clientId, [...held, itemId])
      clientDirectTotals.value = mapWith(clientDirectTotals.value, clientId, (clientDirectTotals.value.get(clientId) ?? 0) + 1)
    }
    // A client is one of the four homes, so this document is not attached to nothing any more.
    dropUnattached(itemId)
    // A document in a chat thread that gains a client STAYS in the conversation group — the thread
    // is still one of its homes (AlexAmin/marcus#150). This used to evict it, which is the same
    // fallback reading the server's `conversationOnly` CTE had.
  }

  /** Drop a direct client link locally. Nothing else is inferred from it — whether the document is
   *  attached elsewhere is not knowable from here, and `fetchGroups` reconciles. */
  function unlinkFromClient(clientId: string, itemId: string) {
    const held = clientDirectIds.value.get(clientId)
    if (!held?.includes(itemId)) return
    clientDirectIds.value = mapWith(clientDirectIds.value, clientId, held.filter(x => x !== itemId))
    clientDirectTotals.value = mapWith(clientDirectTotals.value, clientId, Math.max(0, (clientDirectTotals.value.get(clientId) ?? 1) - 1))
  }

  /**
   * Seed the org-wide document set the grouped views draw from.
   *
   * One listing endpoint, not two (MAR-93): this used to call `/api/items/groups`, a separate route
   * over a separate items projection in the clients service, whose only remaining job was to return
   * this same set plus a server-computed `ungroupedIds`. Every bucket is now derived from the row's
   * own membership arrays (`bucketOrgDocuments`) — no second endpoint, and no `by` parameter, since
   * both modes always needed the same documents.
   *
   * Folders mode used to skip this outright, on the reasoning that it renders no groups. It still
   * renders none — but it now names the two buckets that have no folder to live in, and a signpost
   * has to be able to say whether there is anything behind it (AlexAmin/marcus#145).
   */
  async function fetchGroups() {
    if (groupedIds.value.length === 0) groupsLoading.value = true
    groupsState.value = 'loading'
    try {
      // The grouped views are sorted and filtered BY THE SERVER since AlexAmin/marcus#169.
      // `groupedIds` comes back in the requested order and is what `useDocumentGroups`' `resolve`
      // ranks by, so no band re-sorts anything client-side any more.
      const payload = await apiFetch<OrgDocumentsPayload>('/api/items', {
        params: { scope: 'org', limit: PAGE_SIZE, ...listingParams.value },
      })
      const buckets = bucketOrgDocuments(payload.documents)
      // Seeded once from the server, then maintained locally by mark/unmarkUngrouped.
      ungroupedIds.value = buckets.ungroupedIds
      ungroupedTotal.value = payload.ungroupedTotal
      conversationLinkedIds.value = buckets.conversationLinkedIds
      conversationTotal.value = payload.conversationTotal
      sheetLinkedIds.value = buckets.sheetLinkedIds
      sheetTotal.value = payload.sheetTotal
      sheetDocumentIds.value = buckets.sheetDocumentIds
      sheetGroupsMeta.value = payload.sheets ?? []
      conversationDocumentIds.value = buckets.conversationDocumentIds
      conversationGroupsMeta.value = payload.conversations ?? []
      unattachedIds.value = buckets.unattachedIds
      unattachedTotal.value = payload.unattachedTotal
      clientDirectIds.value = buckets.clientDirectIds
      clientDirectTotals.value = new Map(Object.entries(payload.clientTotals ?? {}))
      caseFilteredTotals.value = new Map(Object.entries(payload.caseTotals ?? {}))
      cache.upsert(buckets.rows)
      groupedIds.value = buckets.groupedIds
      // Grouped mode renders a DIFFERENT set from the flat listing. Without tracking these, a
      // document indexed while the sidebar is grouped sits on "Procesando" until a manual refresh.
      trackPending(buckets.rows)
      groupsState.value = 'ready'
    } catch (err) {
      // "tracked by useApi" was false here too, and this is the costliest of the seven because
      // GROUPED IS THE SIDEBAR'S DEFAULT MODE (AlexAmin/marcus#469). Every bucket is seeded from
      // this one request, so a 500 left all of them empty, `useDocumentGroups` resolved zero groups,
      // and `BrowserGroupedList` rendered its empty message verbatim: *No documents are linked to a
      // case yet.* A firm with two hundred matters was told it had none, in a sentence that reads as
      // a fact about their account rather than a fact about one request.
      groupsState.value = 'failed'
      console.error('[documents] /api/items?scope=org failed', err)
    } finally {
      groupsLoading.value = false
    }
  }

  /** Empty every bucket — the folder listing was chosen, and `fetchGroups` refills the signposts. */
  function resetGroups() {
    groupedIds.value = []
    ungroupedIds.value = []
    ungroupedTotal.value = 0
    conversationLinkedIds.value = []
    conversationTotal.value = 0
    sheetDocumentIds.value = new Map()
    sheetGroupsMeta.value = []
    sheetLinkedIds.value = []
    sheetTotal.value = 0
    conversationDocumentIds.value = new Map()
    conversationGroupsMeta.value = []
    unattachedIds.value = []
    unattachedTotal.value = 0
    clientDirectIds.value = new Map()
    clientDirectTotals.value = new Map()
    caseFilteredTotals.value = new Map()
  }

  /** Drop removed documents from every bucket. Part of the store's `remove()`. */
  function prune(gone: Set<string>) {
    groupedIds.value = pruneIds(groupedIds.value, gone)
    ungroupedIds.value = pruneIds(ungroupedIds.value, gone)
    conversationLinkedIds.value = pruneIds(conversationLinkedIds.value, gone)
    sheetLinkedIds.value = pruneIds(sheetLinkedIds.value, gone)
    sheetDocumentIds.value = pruneIdLists(sheetDocumentIds.value, gone)
    conversationDocumentIds.value = pruneIdLists(conversationDocumentIds.value, gone)
    unattachedIds.value = pruneIds(unattachedIds.value, gone)
    clientDirectIds.value = pruneIdLists(clientDirectIds.value, gone)
  }

  return {
    groupedIds, groupedRank, groupedDocuments, ungroupedIds, ungroupedDocuments, ungroupedTotal,
    conversationLinkedIds, conversationTotal,
    sheetDocumentIds, sheetGroupsMeta, sheetLinkedIds, sheetTotal,
    conversationDocumentIds, conversationGroupsMeta,
    unattachedIds, unattachedTotal,
    clientDirectIds, clientDirectTotals, caseFilteredTotals,
    groupsLoading, groupsState, groupsFailed,
    markUngrouped, unmarkUngrouped, markUnattached, linkToClient, unlinkFromClient,
    fetchGroups, resetGroups, prune,
  }
}
